import { __classPrivateFieldGet, __classPrivateFieldSet, __esDecorate, __runInitializers, __setFunctionName } from "tslib";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ViewerElementBase } from "./viewerElementBase";
import { ViewerHotSpotResult } from "./viewerBase";
/**
 * Displays child elements at the screen space location of a hotspot in a babylon-viewer.
 * @remarks
 * The babylon-viewer-annotation element must be a child of a babylon-viewer element.
 */
let HTML3DAnnotationElement = (() => {
    var _HTML3DAnnotationElement_hotSpot_accessor_storage;
    let _classDecorators = [customElement("babylon-viewer-annotation")];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = LitElement;
    let _hotSpot_decorators;
    let _hotSpot_initializers = [];
    let _hotSpot_extraInitializers = [];
    var HTML3DAnnotationElement = _classThis = class extends _classSuper {
        /**
         * The name of the hotspot to track.
         */
        get hotSpot() { return __classPrivateFieldGet(this, _HTML3DAnnotationElement_hotSpot_accessor_storage, "f"); }
        set hotSpot(value) { __classPrivateFieldSet(this, _HTML3DAnnotationElement_hotSpot_accessor_storage, value, "f"); }
        /** @internal */
        connectedCallback() {
            super.connectedCallback();
            this._internals.states?.add("invalid");
            this._connectingAbortController?.abort();
            this._connectingAbortController = new AbortController();
            const abortSignal = this._connectingAbortController.signal;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            (async () => {
                // Custom element registration can happen at any time via a call to customElements.define, which means it is possible
                // the parent custom element hasn't been defined yet. This especially can happen if the order of imports and exports
                // results in the parent element being defined after the HTML3DAnnotationElement within the final JS bundle.
                if (this.parentElement?.matches(":not(:defined)")) {
                    await customElements.whenDefined(this.parentElement?.tagName.toLowerCase());
                    // If the element has since been disconnected or reconnected, abort this connection process.
                    if (abortSignal.aborted) {
                        return;
                    }
                }
                if (!(this.parentElement instanceof ViewerElementBase)) {
                    // eslint-disable-next-line no-console
                    console.warn("The babylon-viewer-annotation element must be a child of a babylon-viewer element.");
                    return;
                }
                this._mutationObserver.observe(this, { childList: true, characterData: true });
                this._sanitizeInnerHTML();
                const viewerElement = this.parentElement;
                const hotSpotResult = new ViewerHotSpotResult();
                const updateAnnotation = (this._updateAnnotation = () => {
                    if (this.hotSpot) {
                        if (viewerElement.queryHotSpot(this.hotSpot, hotSpotResult)) {
                            const [screenX, screenY] = hotSpotResult.screenPosition;
                            this.style.transform = `translate(${screenX}px, ${screenY}px)`;
                            this._internals.states?.delete("invalid");
                            if (hotSpotResult.visibility <= 0) {
                                this._internals.states?.add("back-facing");
                            }
                            else {
                                this._internals.states?.delete("back-facing");
                            }
                        }
                        else {
                            this._internals.states?.add("invalid");
                        }
                    }
                });
                this._updateAnnotation();
                viewerElement.addEventListener("viewerrender", updateAnnotation);
                this._viewerAttachment = {
                    dispose() {
                        viewerElement.removeEventListener("viewerrender", updateAnnotation);
                    },
                };
            })();
        }
        /** @internal */
        disconnectedCallback() {
            super.disconnectedCallback();
            this._connectingAbortController?.abort();
            this._connectingAbortController = null;
            this._viewerAttachment?.dispose();
            this._viewerAttachment = null;
            this._internals.states?.add("invalid");
            this._updateAnnotation = null;
        }
        /** @internal */
        // eslint-disable-next-line @typescript-eslint/naming-convention
        render() {
            return html ` <slot><div aria-label="${this.hotSpot} annotation" part="annotation" class="annotation">${this.hotSpot}</div></slot> `;
        }
        /** @internal */
        // eslint-disable-next-line @typescript-eslint/naming-convention
        update(changedProperties) {
            super.update(changedProperties);
            if (changedProperties.has("hotSpot")) {
                this._updateAnnotation?.();
            }
        }
        _sanitizeInnerHTML() {
            if (this.innerHTML.trim().length === 0) {
                this.innerHTML = "";
            }
        }
        constructor() {
            super(...arguments);
            this._internals = this.attachInternals();
            this._mutationObserver = new MutationObserver((mutations) => {
                if (mutations.some((mutation) => mutation.type === "childList")) {
                    this._sanitizeInnerHTML();
                }
            });
            this._viewerAttachment = null;
            this._connectingAbortController = null;
            this._updateAnnotation = null;
            _HTML3DAnnotationElement_hotSpot_accessor_storage.set(this, __runInitializers(this, _hotSpot_initializers, ""));
            __runInitializers(this, _hotSpot_extraInitializers);
        }
    };
    _HTML3DAnnotationElement_hotSpot_accessor_storage = new WeakMap();
    __setFunctionName(_classThis, "HTML3DAnnotationElement");
    (() => {
        const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
        _hotSpot_decorators = [property({ attribute: "hotspot" })];
        __esDecorate(_classThis, null, _hotSpot_decorators, { kind: "accessor", name: "hotSpot", static: false, private: false, access: { has: obj => "hotSpot" in obj, get: obj => obj.hotSpot, set: (obj, value) => { obj.hotSpot = value; } }, metadata: _metadata }, _hotSpot_initializers, _hotSpot_extraInitializers);
        __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
        HTML3DAnnotationElement = _classThis = _classDescriptor.value;
        if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
    })();
    /** @internal */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _classThis.styles = css `
        :host {
            --annotation-foreground-color: black;
            --annotation-background-color: white;
            display: inline-block;
            position: absolute;
            transition: opacity 0.25s;
        }

        :host([hidden]) {
            display: none;
        }

        :host(:state(back-facing)) {
            opacity: 0.2;
        }

        :host(:state(invalid)) {
            display: none;
        }

        .annotation {
            transform: translate(-50%, -135%);
            font-size: 14px;
            padding: 0px 6px;
            border-radius: 6px;
            color: var(--annotation-foreground-color);
            background-color: var(--annotation-background-color);
        }

        .annotation::after {
            content: "";
            position: absolute;
            left: 50%;
            height: 60%;
            aspect-ratio: 1;
            transform: translate(-50%, 110%) rotate(-45deg);
            background-color: inherit;
            clip-path: polygon(0 0, 100% 100%, 0 100%, 0 0);
        }
    `;
    (() => {
        __runInitializers(_classThis, _classExtraInitializers);
    })();
    return HTML3DAnnotationElement = _classThis;
})();
export { HTML3DAnnotationElement };
//# sourceMappingURL=viewerAnnotationElement.js.map